import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Loading } from "@carbon/react";
import { Add } from "@carbon/icons-react";
import Form from "../carbon/Form/Form";
import CertificationRow from "./CertificationRow";
import {
  CertificationsFormFields,
  CertificationFormFields,
  certificationsValidationSchema,
} from "./certificationsValidationSchema";
import { CompanyProfileRequest, CompanyProfileResponse } from "../../types";
import { useUpdateCompanyProfile } from "../../hooks/useCompany";
import { useUser } from "../../context";
import "./certificationsForm.scss";

interface CertificationsFormProps {
  profile: CompanyProfileResponse;
  setIsDirty: (isDirty: boolean) => void;
}

const emptyCertification: CertificationFormFields = {
  certificationName: "",
  expirationDate: "",
  valid: true,
  fileURL: "",
};

/**
 * The Certifications wizard sub-form (`PUT /company/{id}/profile`), rebuilt on Carbon + React
 * Hook Form's `useFieldArray` (Task 9, Profile/Company flow) in place of 407 lines of Tailwind —
 * the SAME `AddressesForm.tsx`/`BankingInformationForm.tsx` (Task 8/9) rewrite shape: ONE
 * `certifications` array, seeded from `profile.certifications`, editable in place via
 * `useFieldArray`, submitted whole. Safe for the identical reason: `CompanyService#
 * updateCompanyCertifications` (backend) already fully REPLACES the list on every save.
 *
 * `onSubmit` spreads `{...profile, ...}`, same as every other wizard sub-form — `taxId` is
 * `@NotBlank` on the backend (Task 2), and every independent save must keep carrying it.
 */
const CertificationsForm = ({
  profile,
  setIsDirty,
}: CertificationsFormProps) => {
  const { t } = useTranslation();
  const { user } = useUser();

  const defaultValues: CertificationsFormFields = useMemo(
    () => ({
      certifications: (profile.certifications || []).map((certification) => ({
        certificationName: certification.certificationName ?? "",
        expirationDate: certification.expirationDate ?? "",
        // Seeded from the EXISTING row's own `valid`, not hardcoded `true` — see
        // `certificationsValidationSchema.ts`'s own docstring for why this matters (a
        // certification already marked invalid must not silently flip back to valid on an
        // unrelated resave of this list).
        valid: Boolean(certification.valid),
        fileURL: certification.fileURL ?? "",
      })),
    }),
    [profile]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<CertificationsFormFields>({
    resolver: yupResolver(certificationsValidationSchema),
    defaultValues,
  });

  // Same re-sync + one-directional dirty-forwarding precedent as AddressesForm.tsx/
  // BasicInformationForm.tsx — see those files' own docstrings/comments for the full rationale.
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (isDirty) {
      setIsDirty(true);
    }
  }, [isDirty, setIsDirty]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "certifications",
  });

  const { mutate: updateProfile, isPending: isSaving } =
    useUpdateCompanyProfile(() => {
      setIsDirty(false);
    });

  const onSubmit = (data: CertificationsFormFields) => {
    updateProfile({
      ...profile,
      companyId: user?.company?.id,
      certifications: data.certifications,
    } as CompanyProfileRequest);
  };

  return (
    <div className="certifications-form">
      <div className="certifications-form__header">
        <h3>{t("companyProfile.certifications")}</h3>
        <Button
          kind="tertiary"
          size="sm"
          renderIcon={Add}
          type="button"
          onClick={() => append(emptyCertification)}
        >
          {t("companyProfile.addCertification")}
        </Button>
      </div>

      {isSaving ? (
        <Loading withOverlay={false} description={t("cta.loading")} small />
      ) : (
        <Form
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          noValidate
          className="certifications-form__form"
        >
          {fields.length > 0 && (
            <div className="certifications-form__rows">
              {fields.map((field, index) => (
                <CertificationRow
                  key={field.id}
                  index={index}
                  control={control}
                  onRemove={() => remove(index)}
                />
              ))}
            </div>
          )}

          <div className="certifications-form__actions">
            <Button type="submit" disabled={isSaving || !isDirty}>
              {t("cta.save")}
            </Button>
          </div>
        </Form>
      )}
    </div>
  );
};

export default CertificationsForm;
